import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { PaginationInfo } from '../../models';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent implements OnChanges {
  @Input() paginationInfo!: PaginationInfo;
  @Input() loading: boolean = false;
  
  @Output() pageChange = new EventEmitter<number>();
  
  visiblePages: number[] = [];
  showEllipsisStart: boolean = false;
  showEllipsisEnd: boolean = false;
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paginationInfo'] && this.paginationInfo) {
      this.calculateVisiblePages();
    }
  }
  
  calculateVisiblePages(): void {
    const current = this.paginationInfo.currentPage;
    const total = this.paginationInfo.lastPage;
    const maxVisible = 5; // Maximum number of page buttons to show
    
    this.visiblePages = [];
    this.showEllipsisStart = false;
    this.showEllipsisEnd = false;
    
    if (total <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= total; i++) {
        this.visiblePages.push(i);
      }
    } else {
      // Calculate range around current page
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      
      // Adjust range if we're near the beginning or end
      if (current <= 3) {
        start = 1;
        end = Math.min(maxVisible, total);
      } else if (current >= total - 2) {
        start = Math.max(1, total - maxVisible + 1);
        end = total;
      }
      
      // Show ellipsis if there are gaps
      this.showEllipsisStart = start > 1;
      this.showEllipsisEnd = end < total;
      
      for (let i = start; i <= end; i++) {
        this.visiblePages.push(i);
      }
    }
  }
  
  onPageClick(page: number): void {
    if (page !== this.paginationInfo.currentPage && !this.loading) {
      this.pageChange.emit(page);
    }
  }
  
  onFirstPage(): void {
    if (this.paginationInfo.currentPage !== 1 && !this.loading) {
      this.pageChange.emit(1);
    }
  }
  
  onPreviousPage(): void {
    if (this.paginationInfo.currentPage > 1 && !this.loading) {
      this.pageChange.emit(this.paginationInfo.currentPage - 1);
    }
  }
  
  onNextPage(): void {
    if (this.paginationInfo.currentPage < this.paginationInfo.lastPage && !this.loading) {
      this.pageChange.emit(this.paginationInfo.currentPage + 1);
    }
  }
  
  onLastPage(): void {
    if (this.paginationInfo.currentPage !== this.paginationInfo.lastPage && !this.loading) {
      this.pageChange.emit(this.paginationInfo.lastPage);
    }
  }
  
  get startRecord(): number {
    return ((this.paginationInfo.currentPage - 1) * this.paginationInfo.perPage) + 1;
  }
  
  get endRecord(): number {
    return Math.min(
      this.paginationInfo.currentPage * this.paginationInfo.perPage,
      this.paginationInfo.total
    );
  }
  
  get isFirstPage(): boolean {
    return this.paginationInfo.currentPage === 1;
  }
  
  get isLastPage(): boolean {
    return this.paginationInfo.currentPage === this.paginationInfo.lastPage;
  }
}
